import React, { FC, memo } from 'react';
import styled from 'styled-components';
import isEqual from 'react-fast-compare';
import { flexRender, ColumnDefTemplate, HeaderContext, SortDirection } from '@tanstack/react-table';
import { defaultColumnStyling, getAlignStyle } from '../utils/getStyling';
import * as DataTableTypes from '../utils/DataTableTypes';
import { Typography } from '../../Typography';
import { DataTableSortsElements } from '../DataTableAdvancedElements/SortsElements';
import { Th } from './Th';
import { sanitizeId } from '../utils/getCellWidth';

interface MemoizeThProps {
  id: string;
  sorted: false | SortDirection;
  header: ColumnDefTemplate<HeaderContext<object, unknown>> | undefined;
  getContext: () => HeaderContext<object, unknown>;
  meta: DataTableTypes.CustomColumnProps | undefined;
  border: DataTableTypes.DataTableBorder | undefined;
  enableSorting: boolean | undefined;
  onToggleSorting: (desc?: boolean | undefined, isMulti?: boolean | undefined) => void;
  getResizeHandler: () => void;
  resetSize: () => void;
  isResizing: boolean;
  canResize: boolean;
  getSize: () => number;
  calculatedTableHeight: number;
  resizeTableColumns: boolean;
}

export const MemoizeTh: FC<React.PropsWithChildren<MemoizeThProps>> = memo(
  ({
    onToggleSorting,
    border,
    calculatedTableHeight,
    sorted,
    id,
    header,
    meta,
    getContext,
    enableSorting,
    getResizeHandler,
    resetSize,
    resizeTableColumns,
    getSize,
    isResizing,
    canResize
  }) => {
    const { ellipsisOnOverflow, defaultWidth, align, sticky } = meta || {};

    const cssWidth = `calc(var(--header-${sanitizeId(id)}-size) * 1px)`;
    const isSticky = !!sticky;
    const fixedTableWidth = resizeTableColumns
      ? {
          width: cssWidth,
          flex: `1 1 ${cssWidth}`,
          minWidth: cssWidth
        }
      : defaultWidth
        ? { width: `${defaultWidth}px`, minWidth: `${defaultWidth}px`, flex: `1 1 ${defaultWidth}px` }
        : {};

    const cellProps = {
      'data-test': `header_${id}`,
      role: 'columnheader',
      ...(isSticky ? { 'data-sticky-td': true } : {}),
      ...(sticky === 'right' ? { 'data-sticky-first-right-td': true } : {}),
      ...(sticky === 'left' ? { 'data-sticky-first-left-td': true } : {}),
      style: {
        ...defaultColumnStyling,
        width: resizeTableColumns ? undefined : getSize(),
        ...getAlignStyle(align),
        ...fixedTableWidth
      }
    };

    const renderedCell = flexRender(header, getContext());
    return (
      <StyledTh {...cellProps} border={border} resizeTableColumns={resizeTableColumns}>
        {ellipsisOnOverflow ? (
          <Typography dataTest="overflow-typography" noWrap className="cell-name">
            {renderedCell}
          </Typography>
        ) : (
          <span className="cell-name">{renderedCell}</span>
        )}
        {enableSorting ? <DataTableSortsElements onToggleSorting={onToggleSorting} sorted={sorted} id={id} /> : ''}

        {canResize && (
          <StyledResizer
            $isResizing={isResizing}
            $calculatedTableHeight={calculatedTableHeight}
            onTouchStart={getResizeHandler() as any}
            onMouseDown={getResizeHandler() as any}
            onDoubleClick={resetSize}
          >
            <div className={`resizer ${isResizing ? 'isResizing' : ''}`} />
          </StyledResizer>
        )}
      </StyledTh>
    );
  },
  (prev, next) => isEqual(prev, next)
);

const StyledTh = styled(Th)`
position: relative
`;

const StyledResizer = styled.div<{ $calculatedTableHeight: number; $isResizing: boolean }>`
  width: 20px;
  height: 100%;
  margin-left: auto;
  opacity: ${({ $isResizing }) => ($isResizing ? 1 : 0)};
  transition: opacity 0.2s;
  position: absolute;
  right: 0;
  top: 0;
  display: flex;
  justify-content: flex-end;
  cursor: col-resize;
  
  &:hover {
    opacity: 1;
  }
  .resizer {
    height: ${({ $calculatedTableHeight }) => $calculatedTableHeight}px;
    width: 3px;
    user-select: none;
    touch-action: none;
    background: ${({ theme }) => theme.colorOutline};
  }
`;
